import { inject, Injectable } from '@angular/core';
import { DataService } from 'src/app/data.service';
import { BaseHelpService } from '@luzula/luzula-lib';

@Injectable({
  providedIn: 'root'
})
export class HelpService extends BaseHelpService {
  private ds = inject(DataService);

  constructor() {
    super();
    // Picker für Feinbodenform
    this.registerPicker('Feinbodenform');
    // Moorsubstrat-Referenztabelle (wird über Extension-Handler als Modal angezeigt)
    this.registerPicker('Moorsubstrat');
    // Torf-/Muddearten-Referenztabelle (wird über Extension-Handler als Modal angezeigt)
    this.registerPicker('TorfMuddeArten');
    // Lade BodenformSuffixe Namen als Liste
    const suffixe = this.ds.getHardcoded('BodenformSuffixe') as {value: string, name: string}[];
    if (suffixe) {
      this.registerList('BodenformSuffix', suffixe);
    }

    // Lade Torfarten als Liste
    const torfarten = this.ds.getHardcoded('Torfarten') as {value: string, name: string}[];
    if (torfarten) {
      this.registerList('Torfart', torfarten);
    }

    // Lade Farben als Liste
    const farben = this.ds.getHardcoded('Farben') as {value: string, name: string}[];
    if (farben) {
      this.registerList('Farbe', farben);
    }

    // Zersetzungsgrad-Referenztabelle (wird über Extension-Handler als Modal angezeigt)
    this.registerPicker('Zersetzungsgrad');

    // Substratfolge-Hilfen: Mudde-Konsistenz
    this.registerText('Konsistenz', `Konsistenz von Mudden und Quellsedimenten (K1–K5)

K1 — flüssig (meist dickflüssig, z.T. auch sülzig mit Fließeigenschaften)
K2 — breiig (Substrat quillt bei geringstem Druck aus der geschlossenen Hand)
K3 — sehr weich (Substrat ist leicht zwischen den Fingern durchpressbar)
K4 — weich (Substrat leicht knetbar)
K5 — steif (Substrat schwer knetbar)`);

    // Substratfolge-Hilfen: Substanzvolumen
    this.registerText('Substanzvolumen', `Substanzvolumen (SV) — Anteil fester Substanz am Gesamtvolumen

1 — < 3 % (Mudden, stark wasserhaltig)
2 — 3–5 %
3 — 5–8 %
4 — 8–12 %
5 — > 12 %`);

    // Substratfolge-Hilfen: Humusgehalt (B-Punkte)
    this.registerText('Humusgehalt', `Humusgehaltsstufen

1 (h1) — humusfrei (0 %)
2 (h2) — schwach humos (1–2 %)
3 (h3) — mittel humos (2–4 %)
4 (h4) — stark humos (4–8 %)
5 (h5) — sehr stark humos (8–15 %)
6 (h6) — organisch (15–30 %)`);

    // Substratfolge-Hilfen: Lagerungsdichte (B-Punkte)
    this.registerText('Lagerungsdichte', `Lagerungsdichtestufen

1 — gering
2 — mittel
3 — hoch`);

    // Relief-Hilfetext: Neigung
    this.registerText('Neigungsstufe', `Hangneigungsstufen nach SEA

≤ 1°    Ebene — Talsand-, Sandersandgebiete
> 1–3°  schwächer gen. Flachhang / Rampe — Grundmoränen
> 3–7°  stärker gen. Flachhang — Endmoränen
> 7–15° schwächer gen. Lehnhang — kuppige Endmoränen
> 15–25° stärker gen. Lehnhang — Talränder
> 25–60° schwächer gen. Steilhang — Kerbtäler
> 60°   stärker gen. Steilhang — Kreide-, Geschiebemergelkliffs`);

    // Relief-Hilfetext: Wölbung
    this.registerText('Woelbung', `Wölbungsstufen für Reliefformen

Die Wölbung setzt sich aus der vertikalen und horizontalen Wölbung zusammen. In der Namenskombination erscheint stets zuerst die vertikale Wölbung.

Horizontalwölbung: x = konvex, g = gestreckt, v = konkav
Vertikalwölbung: x = konvex, g = gestreckt, v = konkav

xx = doppelt konvex    xg = konvex-gestreckt    xv = konvex-konkav
gx = gestreckt-konvex  gg = doppelt gestreckt   gv = gestreckt-konkav
vx = konkav-konvex     vg = konkav-gestreckt    vv = doppelt konkav`);

    // Relief-Hilfetext: Hangrichtung
    this.registerText('Hangrichtung', `Hangrichtung (Exposition)

Die Hangrichtung wird nur ab stärker geneigtem Flachhang (> 3°) berücksichtigt. Sie ist umso wichtiger, je stärker die Neigung ist.

Angabe als Himmelsrichtung: N, NO, O, SO, S, SW, W, NW`);

    // Relief-Hilfetext: Einbindung (für Ebenen/Rampen)
    this.registerText('ReliefformUmgebung', `Einbindung in die Nachbarschaft

Bei Ebenen und Rampen (≤ 3°) wird nach der Grundrißfigur und den Grenzlängenanteilen zu benachbarten höher oder tiefer liegenden Arealen unterschieden:

Weitlage — allseitig eben, große Ausdehnung
Horstlage — herausragend, allseitig abfallend, rundlich
Rückenlage — herausragend, allseitig abfallend, länglich
Riedellage — hochliegend, nach drei Seiten frei und abfallend
Schulterlage — hochliegend, nach einer Seite abfallend
Simslage — zwischen zwei Arealen, eine Seite ansteigend, andere abfallend
Fußlage — tiefliegend, nach einer Seite ansteigend
Nischenlage — eingesenkt, nach drei Seiten ansteigend, einseitig offen
Senkenlage — eingesenkt, rundlich, allseitig geschlossen und ansteigend
Strecksenkenlage — eingesenkt, länglich, allseitig geschlossen
Tallage — eingesenkt, langgestreckt, zweiseitig offen`);

    // Relief-Hilfetext: Hanganschluss
    this.registerText('Hanganschluss', `Hanganschluss (bei stärker geneigten Hängen > 3°)

Gibt Auskunft, ob der Hang Bestandteil eines Voll- oder Hohlreliefs ist:

B-Lage (Böschungslage) — oben: Ebene, unten: Ebene
V-Lage (Vollrelieflage) — oben: Kuppe, unten: Ebene
H-Lage (Hohlrelieflage) — oben: Ebene, unten: Senke
D-Lage (Doppellage) — oben: Kuppe, unten: Senke`);

    // Relief-Hilfetext: Hangteil
    this.registerText('Hangteil', `Hangteil (bei größerer Höhenspanne)

Bei stärker geneigten Hängen mit größerer Höhenspanne wird unterschieden zwischen:

Oberhang — oberer Bereich des Gesamthanges
Mittelhang — mittlerer Bereich des Gesamthanges
Unterhang — unterer Bereich des Gesamthanges`);

    // Relief-Hilfetext: Höhenspanne
    this.registerText('Hoehenspanne', `Höhenspanne des Gesamthanges

Senkrechter Abstand zwischen Hangoberkante und Sohle des Kaltluftvorfluters.

Wird bei stärker geneigten Hängen (> 3°) angegeben.`);

    // Relief-Hilfetext: Innere Rauhheit
    this.registerText('InnereRauhheit', `Innere Rauhheit

Bezieht sich auf Kleinstformen um und unter 10 m Breite und Höhe.

glatt — ohne innere Rauhheit (z.B. Verlandungsmoore)
leicht bewegt — nahezu Ebene, Höhendifferenz 0,5–2 m
kleinwellig — unregelmäßig geschwungen, 2–5 m
mittelwellig — unregelmäßig geschwungen, 5–15 m
großwellig — unregelmäßig geschwungen, > 15 m
kupstig — eckige Formen, dichte Aufeinanderfolge (Jungdünenfelder), 1–15 m
küstendünig — wie kupstig, in Küstennähe mit litoralen Sedimenten
strandwalldünig — Dünen auf Strandwällen (Reffen) mit Schlenken (Riegen)
altdünig — regelhaft geformte Dünen, „ruhige" Oberfläche, 5–25 m
kleinkuppig — Kuppen ohne Hohlformen, 5–15 m
mittelkuppig — Kuppen ohne Hohlformen, 15–25 m
großkuppig — Kuppen ohne Hohlformen, > 25 m
bucklig — Kuppen und Senken
kleingieserartig — Rücken, Lehnhänge und Strecksenken, 5–15 m
mittelgieserartig — Rücken, Lehnhänge und Strecksenken, 15–25 m
ungeordnet versteilt — > 25% Hangversteilungen ohne Horizontalerstreckung
horizontal gestuft — horizontale Hangversteilungen (auch künstliche Terrassen)
hangfaltig — gerundete Hohl-/Vollformen in Gefällrichtung (periglaziär)
hangfurchig — spitze Hohl-/Vollformen in Gefällrichtung (extraperiglaziär)`);

    // Grund- und Stauwasserformen (SEA 95, Darst. A 3-2a / A 3-4a)
    // Werden über Extension-Handler als Matrix-Modal angezeigt
    // Keys entsprechen den Feld-name-Attributen: Wasserform = Grundwasser, Wasserform2 = Stauwasser
    this.registerPicker('Wasserform');
    this.registerPicker('Wasserform2');

    // Hilfetext für Punktnummer
    this.registerText('Nummer', 'Punktnummer frei eingeben, Knopf generiert Punktnummer aus Forstamt, Forstrevier, eingegebener Punktnummer und Kartierjahr.');

    // Hilfetext für Witterung (automatische Wetterdaten)
    this.registerText('Witterung', `Automatisch abgerufene Wetterdaten von Open-Meteo.

🌡️ Temperatur
  frostig (<0°C) · kalt (0–5°C) · kühl (5–12°C)
  mild (12–18°C) · warm (18–25°C) · heiß (>25°C)

💧 Feuchtigkeit (Tagesniederschlag)
  trocken (<1mm) · leicht feucht (1–5mm)
  feucht (5–10mm) · nach Regenperiode (>10mm)

☁️ Himmel (Bewölkung)
  heiter (<20%) · leicht bewölkt (20–50%)
  bewölkt (50–80%) · bedeckt (>80%)
  Bei Sonderwetter: Nebel, Regen, Schnee, Gewitter

💨 Wind
  windstill (<5 km/h) · leichter Wind (5–20 km/h)
  mäßiger Wind (20–40 km/h) · starker Wind (40–60 km/h)
  stürmisch (>60 km/h) — jeweils mit Himmelsrichtung

🌫️ Luftfeuchte (relativ)
  sehr trocken (<30%) · normal (30–60%)
  feucht (60–80%) · hohe Luftfeuchte (>80%)
  schwül nur bei ≥18°C

🌱 Bodenzustand (Niederschlag der letzten 3 Tage)
  trocken (<1mm) · leicht feucht (1–5mm)
  feucht (5–15mm) · nass (15–30mm) · durchnässt (>30mm)`);
  }
}
